'use client';

import { useId, useRef, useState, useEffect, type ChangeEvent, type ClipboardEvent, type CSSProperties, type KeyboardEvent } from 'react';
import { cn } from '../../utils/cn';
import {
  GLASS_INPUT_BG,
  GLASS_INPUT_BG_FOCUS,
  GLASS_INPUT_BORDER,
  GLASS_INPUT_BORDER_FOCUS,
  GLASS_FOCUS_RING,
  ON_GLASS_STRONG,
} from '../../styles/glassTokens';

export interface CodeInputProps {
  length?: number;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  /** Surface tone. `default` = light app canvas; `on-dark` = immersive glass. */
  tone?: 'default' | 'on-dark';
  /**
   * Focus the first box on mount. Combine with a changing React `key` to also
   * re-focus after a reset (e.g. clearing the field on a failed code entry).
   */
  autoFocus?: boolean;
}

export function CodeInput({
  length = 6,
  value,
  defaultValue = '',
  onChange,
  onComplete,
  invalid,
  disabled,
  ariaLabel = 'One-time code',
  tone = 'default',
  autoFocus = false,
}: CodeInputProps) {
  const id = useId();
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const [internal, setInternal] = useState(defaultValue);
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);
  const code = (value ?? internal).slice(0, length);
  const onDark = tone === 'on-dark';

  // Fire onComplete only on the rising edge — once when the code first reaches
  // a complete value — not on every render while it stays complete. Callers
  // typically pass an inline (unstable) onComplete, so without this guard the
  // effect re-runs on every parent re-render and re-invokes onComplete; a
  // caller that re-renders on failure (e.g. an OTP form showing an error) then
  // auto-resubmits in an infinite loop. The ref resets when the code is no
  // longer complete, so a fresh/changed code fires onComplete again.
  const lastCompletedRef = useRef<string | null>(null);
  useEffect(() => {
    if (code.length === length) {
      if (lastCompletedRef.current !== code) {
        lastCompletedRef.current = code;
        onComplete?.(code);
      }
    } else {
      lastCompletedRef.current = null;
    }
  }, [code, length, onComplete]);

  const update = (next: string) => {
    if (value === undefined) setInternal(next);
    onChange?.(next);
  };

  const handleChange = (idx: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const ch = e.target.value.replace(/\D/g, '').slice(-1);
    if (!ch) return;
    const arr = code.padEnd(length, ' ').split('');
    arr[idx] = ch;
    const next = arr.join('').trimEnd();
    update(next);
    if (idx < length - 1) refs.current[idx + 1]?.focus();
  };

  const handleKey = (idx: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const arr = code.padEnd(length, ' ').split('');
      if (arr[idx] !== ' ') {
        arr[idx] = ' ';
      } else if (idx > 0) {
        arr[idx - 1] = ' ';
        refs.current[idx - 1]?.focus();
      }
      update(arr.join('').trimEnd());
      return;
    }
    if (e.key === 'ArrowLeft' && idx > 0) refs.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < length - 1) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    update(pasted);
    refs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  const onDarkBoxStyle = (active: boolean): CSSProperties => ({
    background: active ? GLASS_INPUT_BG_FOCUS : GLASS_INPUT_BG,
    border: `1px solid ${invalid ? '#FCA5A5' : active ? GLASS_INPUT_BORDER_FOCUS : GLASS_INPUT_BORDER}`,
    color: ON_GLASS_STRONG,
    boxShadow: active ? `0 0 0 3px ${GLASS_FOCUS_RING}` : 'none',
  });

  return (
    <div role="group" aria-label={ariaLabel} className="flex w-full gap-2">
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => {
            refs.current[idx] = el;
          }}
          id={`${id}-${idx}`}
          type="text"
          inputMode="numeric"
          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
          autoFocus={autoFocus && idx === 0}
          maxLength={1}
          value={code[idx] ?? ''}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-label={`Digit ${idx + 1}`}
          onChange={handleChange(idx)}
          onKeyDown={handleKey(idx)}
          onPaste={handlePaste}
          onFocus={onDark ? () => setFocusedIdx(idx) : undefined}
          onBlur={onDark ? () => setFocusedIdx((cur) => (cur === idx ? null : cur)) : undefined}
          style={onDark ? onDarkBoxStyle(focusedIdx === idx) : undefined}
          className={cn(
            'h-14 min-w-0 flex-1 rounded-md border text-center text-h3 font-semibold',
            'transition-colors duration-fast outline-none',
            'disabled:cursor-not-allowed',
            onDark
              ? 'disabled:opacity-60'
              : [
                  'bg-white text-text-strong',
                  'focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:border-brand-700',
                  'disabled:bg-surface-muted',
                  invalid ? 'border-danger' : 'border-border',
                ].join(' '),
          )}
        />
      ))}
    </div>
  );
}
